/* Section-local interactions survive Shopify editor section replacement. */
if (!customElements.get('testimonial-split-slider')) {
  customElements.define('testimonial-split-slider', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-testimonial-slide]')];
      this.index = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.style.setProperty('--testimonial-transition', `${Number(this.dataset.transitionMs) || 350}ms`);
      this.onClick = event => {
        if (event.target.closest('[data-testimonial-prev]')) this.select(this.index - 1);
        if (event.target.closest('[data-testimonial-next]')) this.select(this.index + 1);
      };
      this.onBlockSelect = event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.select(index);
      };
      this.onPointerDown = event => {
        if (this.dataset.swipe !== 'true' || event.target.closest('a,button')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      };
      this.onPointerUp = event => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) this.select(this.index + (dx < 0 ? 1 : -1));
      };
      this.onPointerCancel = () => { this.pointerStart = null; };
      this.onVisibilityChange = () => { if (document.hidden) this.stopAutoplay(); else this.startAutoplay(); };
      this.onMouseEnter = () => { this.hovered = true; this.stopAutoplay(); };
      this.onMouseLeave = () => { this.hovered = false; this.startAutoplay(); };
      this.onFocusIn = () => { this.focused = true; this.stopAutoplay(); };
      this.onFocusOut = () => { this.focused = this.contains(document.activeElement); if (!this.focused) this.startAutoplay(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerCancel);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.select(0, false);
      requestAnimationFrame(() => { if (this.isConnected) this.dataset.ready = ''; });
      this.startAutoplay();
    }
    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerCancel);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.reducedMotion || window.Shopify?.designMode || document.hidden || this.hovered || this.focused) return;
      this.autoplayTimer = setTimeout(() => {
        this.select(this.index + 1);
      }, (Number(this.dataset.autoplaySeconds) || 5) * 1000);
    }
    stopAutoplay() { clearTimeout(this.autoplayTimer); }
    select(index, restartAutoplay = true) {
      if (!this.slides.length) return;
      const previous = this.index;
      this.index = (index + this.slides.length) % this.slides.length;
      this.dataset.direction = index < previous ? 'previous' : 'next';
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.toggleAttribute('data-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        if (position === previous && !active) slide.dataset.leaving = this.dataset.direction;
        else delete slide.dataset.leaving;
      });
      const count = this.querySelector('[data-testimonial-current]');
      const progress = this.querySelector('[data-testimonial-progress]');
      if (count) count.textContent = String(this.index + 1);
      if (progress) progress.style.width = `${(this.index + 1) / this.slides.length * 100}%`;
      if (restartAutoplay) this.startAutoplay();
    }
  });
}
// Video tiles load only after the customer asks to play them.
document.addEventListener('click', event => {
  const button = event.target.closest('[data-review-video-play]');
  if (!button) return;
  const wrapper = button.closest('[data-review-video]');
  const player = wrapper?.querySelector('[data-review-video-player]');
  const video = player?.querySelector('video');
  if (!video) return;
  button.hidden = true;
  player.hidden = false;
  video.play().catch(() => { video.controls = true; });
});
if (!customElements.get('expandable-reviews')) {
  customElements.define('expandable-reviews', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-reviews-more]');
      this.masonry = this.querySelector('.testimonials-showcase__masonry');
      this.moreWrap = this.querySelector('[data-reviews-more-wrap]');
      if (!this.button || !this.masonry) return;

      this.cards = [...this.masonry.querySelectorAll('.review-card')];
      this.expanded = this.button.getAttribute('aria-expanded') === 'true';
      this._layoutFrame = 0;
      this._layoutWidth = -1;
      this.setAttribute('data-reviews-enhanced', '');
      this.masonry.setAttribute('data-masonry-enhanced', '');

      // Every review is mounted and positioned once. The control only
      // changes the masonry viewport from 80vh to its
      // full height, so expanding cannot reshuffle the cards.

      this.onClick = () => this.setExpanded(!this.expanded, true);
      this.onBlockSelect = event => {
        const blockId = event.detail?.blockId;
        const card = this.cards.find(item => item.dataset.blockId === blockId);
        if (card && !this.expanded && Number(card.dataset.reviewY || 0) >= this.collapsedHeight) this.setExpanded(true, false);
      };
      this.onImageLoad = () => this.scheduleLayout();
      this.button.addEventListener('click', this.onClick);
      this.masonry.addEventListener('load', this.onImageLoad, true);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.resizeObserver = new ResizeObserver(entries => {
        const width = Math.round(entries[0]?.contentRect.width || 0);
        if (width && width !== this._layoutWidth) this.scheduleLayout();
      });
      this.resizeObserver.observe(this.masonry);
      this.onResize = () => this.scheduleLayout();
      window.addEventListener('resize', this.onResize, { passive: true });
      this.scheduleLayout();
    }

    scheduleLayout() {
      cancelAnimationFrame(this._layoutFrame);
      this._layoutFrame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      if (!this.isConnected || !this.cards?.length) return;
      const width = this.masonry.clientWidth;
      if (!width) return;
      this._layoutWidth = Math.round(width);

      const styles = getComputedStyle(this.masonry);
      const configuredColumns = Math.max(1, Number.parseInt(styles.getPropertyValue('--columns'), 10) || 4);
      const columns = width < 768 ? Math.min(2, configuredColumns) : configuredColumns;
      const gapProperty = width < 768 ? '--review-masonry-gap-mobile' : '--review-masonry-gap';
      const gap = Number.parseFloat(styles.getPropertyValue(gapProperty)) || (width < 768 ? 10 : 30);
      const cardWidth = Math.max(0, (width - gap * (columns - 1)) / columns);
      const heights = Array(columns).fill(0);

      this.cards.forEach(card => {
        card.style.width = `${cardWidth}px`;
        const column = heights.indexOf(Math.min(...heights));
        const x = column * (cardWidth + gap);
        const y = heights[column];
        card.style.setProperty('--review-x', `${x}px`);
        card.style.setProperty('--review-y', `${y}px`);
        card.dataset.reviewY = String(y);
        const height = card.getBoundingClientRect().height;
        heights[column] = y + height + gap;
        card.dataset.reviewBottom = String(y + height);
      });

      this.fullHeight = Math.max(0, Math.max(...heights) - gap);
      this.collapsedHeight = Math.min(this.fullHeight, window.innerHeight * .8);
      this.masonry.style.height = `${this.expanded ? this.fullHeight : this.collapsedHeight}px`;
      this.syncAccessibility();
      this.setAttribute('data-reviews-ready', '');
    }

    syncAccessibility() {
      this.cards?.forEach(card => {
        const clipped = !this.expanded && Number(card.dataset.reviewY || 0) >= this.collapsedHeight;
        card.toggleAttribute('inert', clipped);
        card.setAttribute('aria-hidden', String(clipped));
      });
    }

    setExpanded(expanded, restoreFocus) {
      if (this.expanded === expanded) return;
      this.expanded = expanded;
      this.button.setAttribute('aria-expanded', String(expanded));
      this.button.textContent = expanded ? this.button.dataset.lessLabel : this.button.dataset.moreLabel;
      this.toggleAttribute('data-reviews-expanded', expanded);
      this.syncAccessibility();
      if (this.fullHeight) this.masonry.style.height = `${expanded ? this.fullHeight : this.collapsedHeight}px`;
      if (!expanded) {
        this.cards.filter(card => Number(card.dataset.reviewY || 0) >= this.collapsedHeight).forEach(card => card.querySelectorAll('video').forEach(video => video.pause()));
        if (restoreFocus) this.button.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._layoutFrame);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.button?.removeEventListener('click', this.onClick);
      this.masonry?.removeEventListener('load', this.onImageLoad, true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  });
}
if (!customElements.get('image-hotspot-set')) {
  customElements.define('image-hotspot-set', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-hotspot-index]')];
      this.panels = [...this.querySelectorAll('[data-hotspot-panel]')];
      this.drawer = this.querySelector('.image-hotspots__panels');
      this.phone = window.matchMedia('(max-width: 767px)');
      this.drawer.inert = this.phone.matches;
      if (this.phone.matches) this.drawer.setAttribute('aria-hidden', 'true');
      else this.drawer.removeAttribute('aria-hidden');
      this.onClick = event => {
        if (event.target.closest('[data-hotspot-close]')) { this.closeDrawer(); return; }
        const tab = event.target.closest('[data-hotspot-index]');
        if (tab && this.contains(tab)) {
          this.select(Number(tab.dataset.hotspotIndex));
          if (this.phone.matches) this.openDrawer(tab);
        }
      };
      this.onPointerOver = event => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const tab = event.target.closest('[data-hotspot-index]');
        if (!tab || !this.contains(tab) || tab.contains(event.relatedTarget)) return;
        this.select(Number(tab.dataset.hotspotIndex));
      };
      this.onModalKeydown = event => {
        if (!this.hasAttribute('data-drawer-open')) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeDrawer(); return; }
        if (event.key !== 'Tab') return;
        const focusables = [...this.drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
          .filter(element => !element.closest('[hidden],[inert]') && element.getClientRects().length);
        if (!focusables.length) { event.preventDefault(); this.drawer.focus({ preventScroll: true }); return; }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.drawer.contains(document.activeElement))) {
          event.preventDefault(); last.focus({ preventScroll: true });
        } else if (!event.shiftKey && (document.activeElement === last || !this.drawer.contains(document.activeElement))) {
          event.preventDefault(); first.focus({ preventScroll: true });
        }
      };
      this.onDocumentFocus = event => {
        if (this.hasAttribute('data-drawer-open') && !this.drawer.contains(event.target)) {
          this.drawer.querySelector('.image-hotspots__drawer-close')?.focus({ preventScroll: true });
        }
      };
      this.onViewport = () => {
        this.closeDrawer();
        this.drawer.inert = this.phone.matches;
        if (this.phone.matches) this.drawer.setAttribute('aria-hidden', 'true');
        else this.drawer.removeAttribute('aria-hidden');
      };
      this.onKeydown = event => {
        const tab = event.target.closest('[data-hotspot-index]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = Number(tab.dataset.hotspotIndex);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (current + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + this.tabs.length) % this.tabs.length;
        this.select(next); this.tabs[next]?.focus();
      };
      this.onBlockSelect = event => {
        if (!this.contains(event.target)) return;
        const tabIndex = this.tabs.findIndex(tab => tab.dataset.hotspotBlockId === event.detail?.blockId);
        const benefitPanel = event.target.closest('[data-hotspot-panel]');
        const index = tabIndex >= 0 ? tabIndex : benefitPanel ? this.panels.indexOf(benefitPanel) : -1;
        if (index >= 0) {
          this.select(index);
          if (this.phone.matches) this.openDrawer(this.tabs[index]);
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('keydown', this.onModalKeydown);
      document.addEventListener('focusin', this.onDocumentFocus);
      this.phone.addEventListener?.('change', this.onViewport);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keydown', this.onModalKeydown);
      document.removeEventListener('focusin', this.onDocumentFocus);
      this.phone.removeEventListener?.('change', this.onViewport);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeAttribute('data-drawer-open');
      this.drawer?.removeAttribute('role');
      this.drawer?.removeAttribute('aria-modal');
      if (this.drawer) {
        this.drawer.inert = this.phone.matches;
        if (this.phone.matches) this.drawer.setAttribute('aria-hidden', 'true');
        else this.drawer.removeAttribute('aria-hidden');
      }
      this.restoreBackground();
      document.documentElement.classList.remove('hotspot-drawer-open');
    }
    lockBackground() {
      this.inertedElements = [];
      for (let current = this.drawer; current?.parentElement; current = current.parentElement) {
        for (const sibling of current.parentElement.children) {
          if (sibling === current || sibling.matches?.('.image-hotspots__backdrop') || sibling.inert) continue;
          sibling.inert = true;
          this.inertedElements.push(sibling);
        }
        if (current.parentElement === document.body) break;
      }
    }
    restoreBackground() {
      this.inertedElements?.forEach(element => { element.inert = false; });
      this.inertedElements = [];
    }
    openDrawer(opener) {
      if (!this.drawer) return;
      const wasOpen = this.hasAttribute('data-drawer-open');
      this.opener = opener;
      this.setAttribute('data-drawer-open', '');
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.inert = false;
      this.drawer.removeAttribute('aria-hidden');
      if (!wasOpen) this.lockBackground();
      document.documentElement.classList.add('hotspot-drawer-open');
      (this.drawer.querySelector('.image-hotspots__drawer-close') || this.drawer).focus({ preventScroll: true });
    }
    closeDrawer() {
      if (!this.hasAttribute('data-drawer-open')) return;
      this.removeAttribute('data-drawer-open');
      this.drawer?.removeAttribute('role');
      this.drawer?.removeAttribute('aria-modal');
      this.drawer.inert = this.phone.matches;
      if (this.phone.matches) this.drawer.setAttribute('aria-hidden', 'true');
      else this.drawer.removeAttribute('aria-hidden');
      this.restoreBackground();
      document.documentElement.classList.remove('hotspot-drawer-open');
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }
    select(index) {
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    }
  });
}
if (!customElements.get('collection-product-spotlight')) {
  customElements.define('collection-product-spotlight', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-spotlight-tab]')];
      this.panels = [...this.querySelectorAll('[data-spotlight-panel]')];
      this.activeIndex = Math.max(0, this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
      this.transitionTimers = new Map();
      this.settlePanels();
      this.onClick = event => {
        const tab = event.target.closest('[data-spotlight-tab]');
        if (tab && this.contains(tab)) this.select(this.tabs.indexOf(tab));
        const arrow = event.target.closest('[data-spotlight-scroll]');
        if (arrow && this.contains(arrow)) {
          const scroller = arrow.closest('[data-spotlight-panel]')?.querySelector('[data-spotlight-cards]');
          scroller?.scrollBy({ left: (arrow.dataset.spotlightScroll === 'prev' ? -1 : 1) * scroller.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      };
      this.onKeydown = event => {
        const tab = event.target.closest('[data-spotlight-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = this.tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (current + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + this.tabs.length) % this.tabs.length;
        this.select(next); this.tabs[next]?.focus();
      };
      this.onPointerOver = event => {
        if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const tab = event.target.closest('[data-spotlight-tab]');
        if (tab && this.contains(tab)) this.select(this.tabs.indexOf(tab));
      };
      this.onBlockSelect = event => {
        const index = this.tabs.findIndex(tab => tab.dataset.spotlightBlockId === event.detail?.blockId);
        if (index >= 0) this.select(index);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerover', this.onPointerOver);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerover', this.onPointerOver);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.transitionTimers?.forEach(timer => clearTimeout(timer));
      this.transitionTimers?.clear();
      this.settlePanels();
    }
    settlePanels() {
      this.panels?.forEach((panel, index) => {
        const active = index === this.activeIndex;
        panel.hidden = !active;
        panel.toggleAttribute('inert', !active);
        panel.setAttribute('aria-hidden', String(!active));
        if (active) panel.dataset.spotlightTransition = 'active';
        else delete panel.dataset.spotlightTransition;
      });
    }
    select(index) {
      if (index < 0 || index >= this.tabs.length || index === this.activeIndex) return;
      this.activeIndex = index;
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.panels.forEach((panel, i) => {
        clearTimeout(this.transitionTimers.get(panel));
        this.transitionTimers.delete(panel);
        if (i === index) {
          panel.hidden = false;
          panel.removeAttribute('inert');
          panel.removeAttribute('aria-hidden');
          panel.dataset.spotlightTransition = reducedMotion ? 'active' : 'entering';
          if (!reducedMotion) requestAnimationFrame(() => {
            if (this.isConnected && this.activeIndex === i) panel.dataset.spotlightTransition = 'active';
          });
        } else {
          panel.setAttribute('inert', '');
          panel.setAttribute('aria-hidden', 'true');
          if (reducedMotion || panel.hidden) {
            panel.hidden = true;
            delete panel.dataset.spotlightTransition;
          } else {
            panel.dataset.spotlightTransition = 'leaving';
            this.transitionTimers.set(panel, setTimeout(() => {
              if (this.isConnected && this.activeIndex !== i) {
                panel.hidden = true;
                delete panel.dataset.spotlightTransition;
              }
              this.transitionTimers.delete(panel);
            }, 350));
          }
        }
      });
    }
  });
}
if (!customElements.get('animated-metrics')) {
  customElements.define('animated-metrics', class extends HTMLElement {
    connectedCallback() {
      const generation = (this.animationGeneration || 0) + 1;
      this.animationGeneration = generation;
      if (this.dataset.animate !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode || window.Shopify?.visualPreviewMode) return;
      this.values = [...this.querySelectorAll('[data-metric-value]')];
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.values.forEach(element => {
          const match = element.dataset.metricValue.match(/^(\d+)([^\d]*)$/);
          if (!match) return;
          const target = Number(match[1]);
          // Large merchant-entered figures cannot be rounded safely by Number;
          // leave their exact text intact rather than animating into 1e+N.
          if (!Number.isSafeInteger(target) || target > 999999) return;
          const suffix = match[2];
          const started = performance.now();
          const frame = time => {
            if (!this.isConnected || this.animationGeneration !== generation) return;
            const progress = Math.min(1, (time - started) / 950);
            element.textContent = `${Math.round(target * (1 - (1 - progress) ** 3))}${suffix}`;
            if (progress < 1) requestAnimationFrame(frame);
          };
          requestAnimationFrame(frame);
        });
      }, { threshold: .25 });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.animationGeneration = (this.animationGeneration || 0) + 1;
      this.observer?.disconnect();
    }
  });
}
if (!customElements.get('build-a-bundle')) {
  customElements.define('build-a-bundle', class extends HTMLElement {
    connectedCallback() {
      this.required = Number(this.dataset.required) || 3;
      this.choices = [...this.querySelectorAll('[data-bundle-choice]')];
      this.selected = new Map();
      this.form = this.querySelector('form');
      this.submit = this.form?.querySelector('button[type="submit"]');
      this.inputs = this.querySelector('[data-bundle-inputs]');
      this.slots = [...this.querySelectorAll('[data-bundle-slot]')];
      this.emptySlots = this.slots.map(slot => slot.querySelector('[data-bundle-slot-label]')?.textContent || 'Choose a product');
      this.status = this.querySelector('[data-bundle-status]');
      this.availability = this.querySelector('[data-bundle-availability]');
      this.error = this.querySelector('[data-cart-error]');
      this.mobileBar = this.querySelector('[data-bundle-mobile-bar]');
      this.mobileToggle = this.querySelector('[data-bundle-mobile-toggle]');
      this.mobileContent = this.querySelector('.build-a-bundle__mobile-content');
      this.onResize = () => this.syncMobileContent();
      this.onClick = event => {
        if (event.target.closest('[data-bundle-mobile-toggle]')) {
          const open = !this.mobileBar.hasAttribute('data-open');
          this.mobileBar.toggleAttribute('data-open', open);
          this.mobileToggle?.setAttribute('aria-expanded', String(open));
          this.syncMobileContent();
          return;
        }
        const remove = event.target.closest('[data-bundle-remove]');
        if (remove && this.contains(remove)) {
          const index = this.slots.indexOf(remove.closest('[data-bundle-slot]'));
          const card = [...this.selected.keys()][index];
          if (!card) return;
          this.selected.delete(card);
          this.render();
          if (matchMedia('(max-width: 767px)').matches) this.mobileToggle?.focus();
          else card.querySelector('[data-bundle-toggle]')?.focus();
          return;
        }
        const button = event.target.closest('[data-bundle-toggle]');
        const card = button?.closest('[data-bundle-choice]');
        if (!card || !this.contains(card) || button.disabled) return;
        if (this.selected.has(card)) this.selected.delete(card);
        else if (this.selected.size < this.required && this.variantId(card)) this.selected.set(card, this.variantId(card));
        this.render();
      };
      this.onChange = event => {
        const card = event.target.closest('[data-bundle-choice]');
        if (card && event.target.matches('[data-bundle-variant]')) this.updatePrice(card);
        if (card && this.selected.has(card)) {
          const id = this.variantId(card);
          if (id) this.selected.set(card, id);
          else this.selected.delete(card);
          this.render();
        }
      };
      this.onSubmit = event => {
        if (this.selected.size === this.required) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (this.error) { this.error.hidden = false; this.error.textContent = this.unavailable ? (this.dataset.unavailableLabel || 'This bundle is currently unavailable. Please check back soon.') : `${this.required - this.selected.size} ${this.dataset.remainingLabel || 'more to choose'}`; }
      };
      this.onKeydown = event => {
        if (event.key !== 'Escape' || !this.mobileBar?.hasAttribute('data-open')) return;
        this.mobileBar.removeAttribute('data-open');
        this.mobileToggle?.setAttribute('aria-expanded', 'false');
        this.syncMobileContent();
        this.mobileToggle?.focus();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
      this.form?.addEventListener('submit', this.onSubmit);
      this.busyObserver = new MutationObserver(() => this.render());
      if (this.form) this.busyObserver.observe(this.form, { attributes: true, attributeFilter: ['aria-busy'] });
      this.syncMobileContent();
      this.render();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.busyObserver?.disconnect();
    }
    syncMobileContent() {
      if (!this.mobileContent) return;
      const hidden = matchMedia('(max-width: 767px)').matches && !this.mobileBar?.hasAttribute('data-open');
      this.mobileContent.inert = hidden;
      this.mobileContent.setAttribute('aria-hidden', String(hidden));
    }
    variantId(card) {
      const input = card.querySelector('[data-bundle-variant]');
      if (!input || input.disabled) return '';
      if (input.tagName === 'SELECT' && input.selectedOptions[0]?.disabled) return '';
      return /^\d+$/.test(input.value) ? input.value : '';
    }
    updatePrice(card) {
      const option = card.querySelector('select[data-bundle-variant]')?.selectedOptions[0];
      const price = card.querySelector('.price');
      const current = price?.querySelector('.price__current');
      if (!option?.dataset.price || !current) return;
      current.textContent = option.dataset.price;
      let compare = price.querySelector('.price__compare');
      price.querySelectorAll(':scope > .visually-hidden').forEach(label => label.remove());
      if (option.dataset.comparePrice) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          current.before(compare);
        }
        compare.textContent = option.dataset.comparePrice;
        const regularLabel = document.createElement('span');
        regularLabel.className = 'visually-hidden';
        regularLabel.textContent = card.dataset.bundleRegularPriceLabel || 'Regular price';
        compare.before(regularLabel);
        const saleLabel = document.createElement('span');
        saleLabel.className = 'visually-hidden';
        saleLabel.textContent = card.dataset.bundleSalePriceLabel || 'Sale price';
        current.before(saleLabel);
      } else compare?.remove();
      price.classList.toggle('price--on-sale', Boolean(option.dataset.comparePrice));
      const badge = card.querySelector('[data-bundle-sale-badge]');
      if (badge) badge.hidden = !option.dataset.comparePrice;
    }
    render() {
      const full = this.selected.size >= this.required;
      const availableCount = this.choices.filter(card => Boolean(this.variantId(card))).length;
      const unavailable = availableCount < this.required;
      this.unavailable = unavailable;
      const removableSlots = this.slots.some(slot => slot.querySelector('[data-bundle-remove]'));
      this.choices.forEach(card => {
        const chosen = this.selected.has(card);
        card.toggleAttribute('data-selected', chosen);
        const button = card.querySelector('[data-bundle-toggle]');
        if (!button) return;
        button.disabled = (chosen && removableSlots) || (!chosen && full) || !this.variantId(card);
        button.setAttribute('aria-disabled', String(chosen && removableSlots));
        button.setAttribute('aria-pressed', String(chosen));
        button.setAttribute('aria-label', chosen ? `${this.dataset.removeLabel || 'Remove'} ${card.dataset.bundleTitle || 'product'}` : `${this.dataset.addLabel || 'Add'} ${card.dataset.bundleTitle || 'product'}`);
        button.textContent = this.dataset.addLabel || 'Add';
      });
      this.inputs?.replaceChildren();
      [...this.selected].forEach(([card, id], index) => {
        for (const [field, value] of [['id', id], ['quantity', '1']]) {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = `items[${index}][${field}]`; input.value = value;
          this.inputs?.append(input);
        }
      });
      this.slots.forEach((slot, index) => {
        const card = [...this.selected.keys()][index];
        slot.toggleAttribute('data-filled', Boolean(card));
        const label = slot.querySelector('[data-bundle-slot-label]');
        if (label) label.textContent = card?.dataset.bundleTitle || this.emptySlots[index];
        const remove = slot.querySelector('[data-bundle-remove]');
        if (remove) {
          remove.hidden = !card;
          remove.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'} ${card?.dataset.bundleTitle || 'product'} from bundle`);
        }
        const imageWrap = slot.querySelector('[data-bundle-slot-image]');
        if (imageWrap) {
          imageWrap.replaceChildren();
          const image = card?.querySelector('.build-a-bundle__primary-image, .build-a-bundle__image img');
          if (image) {
            const clone = image.cloneNode();
            clone.removeAttribute('srcset'); clone.removeAttribute('sizes'); clone.alt = '';
            const variantImage = card.querySelector('select[data-bundle-variant]')?.selectedOptions[0]?.dataset.image;
            if (variantImage) clone.src = variantImage;
            imageWrap.append(clone);
          }
        }
      });
      if (this.status) this.status.textContent = `${this.required - this.selected.size} ${this.dataset.remainingLabel || 'more to choose'}`;
      if (this.availability) {
        const showUnavailable = unavailable && this.dataset.hideUnavailableNote !== 'true';
        this.availability.hidden = !showUnavailable;
        this.availability.textContent = showUnavailable ? (this.dataset.unavailableLabel || 'This bundle is currently unavailable. Please check back soon.') : '';
      }
      const count = this.querySelector('[data-bundle-count]');
      if (count) count.textContent = String(this.selected.size);
      if (this.submit) this.submit.disabled = unavailable || !full || this.form.hasAttribute('aria-busy');
      if (this.error) this.error.hidden = true;
    }
  });
}
